import { withRequestDeadline } from "../src/utils/requestDeadline";

type SupportUploadResponse = {
  message?: string;
  [key: string]: unknown;
};

type SupportUploadInitResponse = SupportUploadResponse & {
  uploadUrl?: string;
  imageHash?: string;
  category?: string;
};

function getApiBaseUrl(): string {
  return (import.meta.env.VITE_API_ARTIFACT_URL || "").trim();
}

function resolveSupportUploadEndpoint(overrideEndpointUrl?: string): string {
  const override = (overrideEndpointUrl || "").trim();
  if (override) {
    return override;
  }

  const baseUrl = getApiBaseUrl();
  return baseUrl ? `${baseUrl}/image` : "";
}

function resolveSupportMetadataEndpoint(overrideEndpointUrl?: string): string {
  const override = (overrideEndpointUrl || "").trim();
  if (override) {
    return override;
  }

  const baseUrl = getApiBaseUrl();
  return baseUrl ? `${baseUrl}/image/metadata` : "";
}

const SUPPORT_UPLOAD_PAYLOAD_FIELDS = {
  fileName: "fileName",
  fileContentType: "fileContentType",
  userName: "userName",
  platform: "platform",
  client: "client",
} as const;

function normalizeUploadFileName(fileName: string): string {
  return fileName.toLowerCase().endsWith(".webp")
    ? fileName.replace(/\.webp$/i, ".png")
    : fileName;
}

async function parseApiResponse(
  response: Response,
): Promise<SupportUploadResponse> {
  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    return (await response.json()) as SupportUploadResponse;
  }

  return { message: await response.text() };
}

async function notifyUploadFailure(params: {
  endpointUrl: string;
  token?: string;
  dbId?: string;
  category?: string;
}): Promise<void> {
  const { endpointUrl, token, dbId, category } = params;
  if (!dbId) {
    return;
  }

  await withRequestDeadline((signal) =>
    fetch(endpointUrl, {
      method: "POST",
      body: JSON.stringify({
        message: "upload failed",
        dbId,
        category: category || "",
      }),
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      signal,
    }),
  );
}

async function uploadFileToPresignedUrl(params: {
  uploadUrl: string;
  file: File;
  fileContentType: string;
}): Promise<void> {
  const { uploadUrl, file, fileContentType } = params;
  const response = await withRequestDeadline((signal) =>
    fetch(uploadUrl, {
      method: "PUT",
      body: file,
      headers: {
        "Content-Type": fileContentType || "application/octet-stream",
      },
      signal,
    }),
  );

  if (!response.ok) {
    const responseText = await response.text();
    throw new Error(
      responseText || `Presigned upload failed with status ${response.status}`,
    );
  }
}

export async function uploadSupportImage(params: {
  endpointUrl?: string;
  token?: string;
  file: File;
  userName: string;
  platform?: string;
  client?: string;
}): Promise<SupportUploadResponse> {
  const { endpointUrl, token, file, userName, platform, client } = params;
  const resolvedEndpointUrl = resolveSupportUploadEndpoint(endpointUrl);

  if (!resolvedEndpointUrl) {
    throw new Error("Support upload endpoint is not configured.");
  }

  const uploadFileName = normalizeUploadFileName(file.name);
  const payload: Record<string, string> = {
    [SUPPORT_UPLOAD_PAYLOAD_FIELDS.fileName]: uploadFileName,
    [SUPPORT_UPLOAD_PAYLOAD_FIELDS.fileContentType]:
      file.type || "application/octet-stream",
    [SUPPORT_UPLOAD_PAYLOAD_FIELDS.userName]: userName,
    [SUPPORT_UPLOAD_PAYLOAD_FIELDS.platform]: platform || "",
    [SUPPORT_UPLOAD_PAYLOAD_FIELDS.client]: client || "",
  };

  const initResponse = await withRequestDeadline((signal) =>
    fetch(resolvedEndpointUrl, {
      method: "POST",
      body: JSON.stringify(payload),
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      signal,
    }),
  );

  if (!initResponse.ok) {
    const responseText = await initResponse.text();
    throw new Error(
      responseText ||
        `Support upload failed with status ${initResponse.status}`,
    );
  }

  const initBody = (await parseApiResponse(
    initResponse,
  )) as SupportUploadInitResponse;
  const presignedUploadUrl = String(initBody.uploadUrl || "").trim();

  if (!presignedUploadUrl) {
    throw new Error("Upload URL was not returned by the server.");
  }

  try {
    await uploadFileToPresignedUrl({
      uploadUrl: presignedUploadUrl,
      file,
      fileContentType: file.type || "application/octet-stream",
    });
  } catch (error) {
    await notifyUploadFailure({
      endpointUrl: resolvedEndpointUrl,
      token,
      dbId: initBody.imageHash,
      category:
        typeof initBody.category === "string" ? initBody.category : undefined,
    });
    throw error;
  }

  const {
    uploadUrl: _uploadUrl,
    uploadMethod: _uploadMethod,
    expiresInSeconds: _expiresInSeconds,
    ...safeResponse
  } = initBody;
  return safeResponse;
}

export async function getSupportImageMetadata(params: {
  endpointUrl?: string;
  token?: string;
  platform?: string;
  client?: string;
}): Promise<SupportUploadResponse> {
  const { endpointUrl, token, platform, client } = params;
  const resolvedEndpointUrl = resolveSupportMetadataEndpoint(endpointUrl);

  if (!resolvedEndpointUrl) {
    throw new Error("Image metadata endpoint is not configured.");
  }

  const searchParams = new URLSearchParams({
    platform: platform || "",
    client: client || "",
  });

  const response = await withRequestDeadline((signal) =>
    fetch(`${resolvedEndpointUrl}?${searchParams.toString()}`, {
      method: "GET",
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      signal,
    }),
  );

  if (!response.ok) {
    const responseText = await response.text();
    throw new Error(
      responseText || `Metadata request failed with status ${response.status}`,
    );
  }

  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    return (await response.json()) as SupportUploadResponse;
  }

  return { message: await response.text() };
}
